import React, { useState, useEffect } from 'react';
import { ArrowUpRight, Linkedin, Mail, Monitor, Globe, Wifi, WifiOff, Languages, Accessibility, Volume2, Moon, Sun, Menu, X, AppWindow, Paintbrush, BarChart3 } from 'lucide-react';
import Seo from './Seo.tsx';
import ContactForm from './ContactForm.tsx';

/**
 * Dedicated landing page for interactive kiosk and installation services.
 * Targets creative agencies working with museums and cultural institutions.
 * Designed to position Ben as a specialist in this niche.
 */
const KiosksPage: React.FC = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem('darkMode');
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    if (stored === 'true' || stored === 'false') {
      setIsDarkMode(stored === 'true');
    } else {
      setIsDarkMode(mediaQuery.matches);
    }
  }, []);

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  const toggleDarkMode = () => {
    const newValue = !isDarkMode;
    setIsDarkMode(newValue);
    localStorage.setItem('darkMode', newValue.toString());
  };

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  return (
    <div className={`min-h-screen bg-neutral-50 dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 font-sans ${isLoaded ? 'animate-fadeInUp' : 'opacity-0'}`}>
      <Seo
        title="Ben Smith | Kiosk & Installation Development for Museums"
        description="Kiosk and installation software for museums, visitor centers, and public spaces. Offline-first, multilingual, accessible. 7 kiosks installed, 12 languages shipped. Built with and for creative agencies."
        path="/kiosks"
      />

      {/* Navigation */}
      <nav className="bg-white/80 dark:bg-neutral-900/80 backdrop-blur-md py-4 px-6 sticky top-0 shadow-soft z-50 border-b border-primary-100 dark:border-neutral-700">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <a href="/" className="text-2xl font-bold">
            <span className="text-neutral-900 dark:text-neutral-100">Ben Smith</span>
            <span className="text-primary-600 dark:text-primary-400"> Development</span>
          </a>
          <div className="hidden md:flex space-x-8 items-center">
            <a href="/" className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200 font-medium rounded-md px-3 py-1.5">
              Home
            </a>
            <button onClick={() => scrollToSection('case-studies')} className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200 font-medium rounded-md px-3 py-1.5">
              Case Studies
            </button>
            <button onClick={() => scrollToSection('capabilities')} className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200 font-medium rounded-md px-3 py-1.5">
              Capabilities
            </button>
            <a href="/about" className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200 font-medium rounded-md px-3 py-1.5">
              About
            </a>
            <button onClick={() => scrollToSection('contact')} className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200 font-medium rounded-md px-3 py-1.5">
              Contact
            </button>
            <button
              onClick={toggleDarkMode}
              className="transition-all duration-200 font-medium hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-md px-3 py-1.5"
              aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
            >
              {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
            </button>
          </div>
          <button
            className="md:hidden p-2 hover:bg-primary-50 dark:hover:bg-neutral-800 rounded-lg transition-colors"
            onClick={toggleMenu}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMenuOpen ? <X size={24} className="text-neutral-700 dark:text-neutral-300" /> : <Menu size={24} className="text-neutral-700 dark:text-neutral-300" />}
          </button>
        </div>
        {isMenuOpen && (
          <div className="md:hidden absolute top-16 left-0 right-0 bg-white/95 dark:bg-neutral-900/95 backdrop-blur-md border-b border-primary-100 dark:border-neutral-700 p-4 shadow-soft rounded-b-2xl z-50">
            <div className="flex flex-col space-y-4">
              <a href="/" className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors font-medium px-3 py-2 rounded-md" onClick={toggleMenu}>Home</a>
              <button onClick={() => scrollToSection('case-studies')} className="text-left text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors font-medium px-3 py-2 rounded-md">Case Studies</button>
              <button onClick={() => scrollToSection('capabilities')} className="text-left text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors font-medium px-3 py-2 rounded-md">Capabilities</button>
              <a href="/about" className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors font-medium px-3 py-2 rounded-md" onClick={toggleMenu}>About</a>
              <button onClick={() => scrollToSection('contact')} className="text-left text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors font-medium px-3 py-2 rounded-md">Contact</button>
            </div>
          </div>
        )}
      </nav>

      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-neutral-900 via-neutral-800 to-primary-900 text-white py-28 md:py-36 px-6 overflow-hidden">
          {/* Subtle grid overlay for museum/institutional feel */}
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '60px 60px'
          }}></div>
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/40 to-transparent"></div>

          <div className="relative max-w-5xl mx-auto z-10">
            <div className="inline-block mb-6 px-4 py-1.5 bg-primary-500/20 border border-primary-400/30 rounded-full">
              <span className="text-primary-300 text-sm font-medium tracking-wide uppercase">Interactive installations</span>
            </div>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-[1.1] tracking-tight">
              Kiosk & Installation<br />
              <span className="text-primary-400">Development</span>
            </h1>
            <p className="text-xl md:text-2xl max-w-2xl mb-6 text-neutral-300 leading-relaxed">
              Touchscreen software for museums, visitor centers, and public spaces. Offline-first. Multilingual. Accessible.
            </p>
            <p className="text-lg max-w-2xl mb-10 text-neutral-400">
              I'm the technical build partner for creative agencies — from a single kiosk in a lobby to multi-station installations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button onClick={() => scrollToSection('case-studies')} className="bg-primary-600 hover:bg-primary-500 text-white font-semibold py-3.5 px-8 rounded-full hover:shadow-lg hover:scale-105 transition-all duration-300 text-center">
                See case studies
              </button>
              <button onClick={() => scrollToSection('contact')} className="bg-transparent border-2 border-white/30 text-white font-semibold py-3.5 px-8 rounded-full hover:bg-white/10 hover:border-white/50 transition-all duration-300 text-center">
                Discuss a project
              </button>
            </div>
          </div>
        </section>

        {/* Quick credibility bar */}
        <section className="bg-white dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700 py-8 px-6">
          <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-center gap-6 md:gap-16 text-center">
            <div>
              <div className="text-3xl font-bold text-primary-600 dark:text-primary-400">15+</div>
              <div className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">Years experience</div>
            </div>
            <div className="hidden md:block w-px h-10 bg-neutral-200 dark:bg-neutral-700"></div>
            <div>
              <div className="text-3xl font-bold text-primary-600 dark:text-primary-400">7</div>
              <div className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">Kiosks installed</div>
            </div>
            <div className="hidden md:block w-px h-10 bg-neutral-200 dark:bg-neutral-700"></div>
            <div>
              <div className="text-3xl font-bold text-primary-600 dark:text-primary-400">12</div>
              <div className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">Languages supported</div>
            </div>
            <div className="hidden md:block w-px h-10 bg-neutral-200 dark:bg-neutral-700"></div>
            <div>
              <div className="text-3xl font-bold text-primary-600 dark:text-primary-400">100%</div>
              <div className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">Offline capable</div>
            </div>
          </div>
        </section>

        {/* The Problem / Value Proposition */}
        <section className="py-24 px-6 bg-neutral-50 dark:bg-neutral-900">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-primary-700 dark:text-gray-100">
              Your agency designs the experience.<br />I make it work in the real world.
            </h2>
            <p className="text-lg text-neutral-600 dark:text-neutral-300 leading-relaxed max-w-3xl mx-auto">
              Public installations fail in ways websites don't. No reliable network. Hardware constraints. Visitors of every language and ability. Content that staff need to edit without calling a developer. I've shipped against all of it, alongside the agency's design and content teams.
            </p>
          </div>
        </section>

        {/* Case Studies */}
        <section id="case-studies" className="py-24 px-6 bg-neutral-50 dark:bg-neutral-900">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-16 text-center text-primary-700 dark:text-gray-100">
              Case studies
            </h2>

            {/* Case Study 1: Central Platte NRD */}
            <div className="mb-20">
              <div className="bg-neutral-50 dark:bg-neutral-900 rounded-2xl overflow-hidden border border-neutral-200 dark:border-neutral-700">
                <div className="bg-gradient-to-r from-emerald-700 to-teal-900 p-8 md:p-12">
                  <div className="text-emerald-200 text-sm font-medium tracking-wide uppercase mb-3">Central Platte Natural Resources District</div>
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">Interactive Visitor Center Kiosk</h3>
                  <p className="text-emerald-100 text-lg">Touchscreen experience built into a sculptural tree · Design by McCullough Creative</p>
                </div>

                {/* Image */}
                <div className="px-8 md:px-12 py-8">
                  <div className="max-w-3xl mx-auto rounded-lg overflow-hidden shadow-lg">
                    <img
                      src="/portfolio-images/cpnrd-tree.jpg"
                      alt="CPNRD visitor center kiosk installation — touchscreen embedded in a sculptural wooden tree"
                      className="w-full h-auto"
                    />
                  </div>
                </div>

                <div className="p-8 md:p-12">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                    <div>
                      <h4 className="font-bold text-lg mb-4 text-neutral-900 dark:text-neutral-100">The challenge</h4>
                      <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed mb-6">
                        The centerpiece of CPNRD's visitor space is a sculptural wooden tree with a touchscreen built directly into its trunk. The interactive had to live up to that setting: matching the exhibit's hand-painted watercolor aesthetic in motion, guiding visitors with very different interests to the right content, and running unattended in a public lobby — while still reporting how visitors actually use it.
                      </p>
                      <h4 className="font-bold text-lg mb-4 text-neutral-900 dark:text-neutral-100">The solution</h4>
                      <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed">
                        I built a native kiosk app paired with a cloud CMS: staff update content remotely, the kiosk runs fully offline. A custom watercolor animation engine carries McCullough Creative's hand-painted style into every interaction — ink-wash blooms on each screen, a watercolor finale to close each session. Visitor analytics sync to the cloud when a connection is available, with an on-device dashboard as fallback.
                      </p>
                    </div>
                    <div>
                      <h4 className="font-bold text-lg mb-4 text-neutral-900 dark:text-neutral-100">Key features</h4>
                      <div className="space-y-4">
                        <div className="flex items-start gap-3">
                          <div className="mt-1 p-1.5 bg-emerald-100 dark:bg-emerald-900/40 rounded-lg">
                            <Paintbrush size={18} className="text-emerald-600 dark:text-emerald-400" />
                          </div>
                          <div>
                            <div className="font-semibold text-neutral-900 dark:text-neutral-100">Watercolor Animation Engine</div>
                            <div className="text-sm text-neutral-500 dark:text-neutral-400">Timed, randomized ink-wash animations that extend the exhibit's hand-painted style into every interaction</div>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="mt-1 p-1.5 bg-emerald-100 dark:bg-emerald-900/40 rounded-lg">
                            <WifiOff size={18} className="text-emerald-600 dark:text-emerald-400" />
                          </div>
                          <div>
                            <div className="font-semibold text-neutral-900 dark:text-neutral-100">Offline-First, Cloud-Managed</div>
                            <div className="text-sm text-neutral-500 dark:text-neutral-400">Native app syncs content from a cloud-based CMS when online and runs independently when it's not</div>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="mt-1 p-1.5 bg-emerald-100 dark:bg-emerald-900/40 rounded-lg">
                            <BarChart3 size={18} className="text-emerald-600 dark:text-emerald-400" />
                          </div>
                          <div>
                            <div className="font-semibold text-neutral-900 dark:text-neutral-100">Built-in Analytics</div>
                            <div className="text-sm text-neutral-500 dark:text-neutral-400">Visitor interactions sync to the cloud, with an on-device analytics dashboard for admins as a fallback</div>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="mt-1 p-1.5 bg-emerald-100 dark:bg-emerald-900/40 rounded-lg">
                            <Accessibility size={18} className="text-emerald-600 dark:text-emerald-400" />
                          </div>
                          <div>
                            <div className="font-semibold text-neutral-900 dark:text-neutral-100">Custom Accessibility Widget</div>
                            <div className="text-sm text-neutral-500 dark:text-neutral-400">Adjustable contrast and font sizing tailored to a public touchscreen environment</div>
                          </div>
                        </div>
                      </div>
                      <div className="mt-6 pt-6 border-t border-neutral-200 dark:border-neutral-700">
                        <div className="text-xs text-neutral-400 dark:text-neutral-500 uppercase tracking-wide mb-2">Tech stack</div>
                        <div className="flex flex-wrap gap-2">
                          {['Custom Native App', 'Cloud CMS Sync', 'Watercolor Animation', 'Offline-First', 'Built-in Analytics', 'Touch UI', 'Accessibility'].map((tag) => (
                            <span key={tag} className="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-xs px-2.5 py-1 rounded-full font-medium">{tag}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Video Walkthrough */}
                <div className="px-8 md:px-12 pb-8">
                  <div className="max-w-3xl mx-auto rounded-lg overflow-hidden shadow-lg">
                    <video
                      autoPlay
                      loop
                      muted
                      playsInline
                      poster="/portfolio-images/cpnrd-video-poster.jpg"
                      className="w-full h-auto"
                    >
                      <source src="/videos/cpnrd-walkthrough.webm" type="video/webm" />
                      <source src="/videos/cpnrd-walkthrough.mp4" type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                </div>
              </div>
            </div>

            {/* Case Study 2: University of Northern Iowa */}
            <div className="mb-20">
              <div className="bg-neutral-50 dark:bg-neutral-900 rounded-2xl overflow-hidden border border-neutral-200 dark:border-neutral-700">
                <div className="bg-gradient-to-r from-primary-700 to-primary-900 p-8 md:p-12">
                  <div className="text-primary-200 text-sm font-medium tracking-wide uppercase mb-3">University of Northern Iowa</div>
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">150th Anniversary Interactive Kiosk System</h3>
                  <p className="text-primary-100 text-lg">Multi-kiosk installation celebrating 150 years of university history · Design by McCullough Creative</p>
                </div>

                {/* Image */}
                <div className="px-8 md:px-12 py-8">
                  <div className="max-w-3xl mx-auto rounded-lg overflow-hidden shadow-lg">
                    <img
                      src="/portfolio-images/uni-installation.jpg"
                      alt="University of Northern Iowa 150th Anniversary Kiosk Installation"
                      className="w-full h-auto"
                    />
                  </div>
                </div>

                <div className="p-8 md:p-12">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                    <div>
                      <h4 className="font-bold text-lg mb-4 text-neutral-900 dark:text-neutral-100">The challenge</h4>
                      <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed mb-6">
                        The university needed touchscreen kiosks that run reliably offline in public spaces, with content staff could update remotely through a CMS they already knew.
                      </p>
                      <h4 className="font-bold text-lg mb-4 text-neutral-900 dark:text-neutral-100">The solution</h4>
                      <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed">
                        I built a system where content editors manage everything through WordPress and Elementor — tools they already know. Behind the scenes, a custom pipeline automatically generates static versions of the content. Each kiosk runs a custom Electron application on Mac Minis with touch screens, syncing the latest content whenever a network connection is available and serving it locally when it's not.
                      </p>
                    </div>
                    <div>
                      <h4 className="font-bold text-lg mb-4 text-neutral-900 dark:text-neutral-100">Key features</h4>
                      <div className="space-y-4">
                        <div className="flex items-start gap-3">
                          <div className="mt-1 p-1.5 bg-primary-100 dark:bg-primary-900/40 rounded-lg">
                            <WifiOff size={18} className="text-primary-600 dark:text-primary-400" />
                          </div>
                          <div>
                            <div className="font-semibold text-neutral-900 dark:text-neutral-100">Offline-First Architecture</div>
                            <div className="text-sm text-neutral-500 dark:text-neutral-400">Custom Electron app on Mac Minis with automatic content sync when online</div>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="mt-1 p-1.5 bg-primary-100 dark:bg-primary-900/40 rounded-lg">
                            <Monitor size={18} className="text-primary-600 dark:text-primary-400" />
                          </div>
                          <div>
                            <div className="font-semibold text-neutral-900 dark:text-neutral-100">Remote CMS Management</div>
                            <div className="text-sm text-neutral-500 dark:text-neutral-400">WordPress + Elementor backend with automated static generation</div>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="mt-1 p-1.5 bg-primary-100 dark:bg-primary-900/40 rounded-lg">
                            <Accessibility size={18} className="text-primary-600 dark:text-primary-400" />
                          </div>
                          <div>
                            <div className="font-semibold text-neutral-900 dark:text-neutral-100">Full Accessibility Suite</div>
                            <div className="text-sm text-neutral-500 dark:text-neutral-400">Custom widget with adjustable contrast, font sizing, dyslexia-friendly fonts, and a screen reader using native speech synthesis with on-screen highlighting</div>
                          </div>
                        </div>
                      </div>
                      <div className="mt-6 pt-6 border-t border-neutral-200 dark:border-neutral-700">
                        <div className="text-xs text-neutral-400 dark:text-neutral-500 uppercase tracking-wide mb-2">Tech stack</div>
                        <div className="flex flex-wrap gap-2">
                          {['Electron', 'React', 'Offline-First Sync', 'Remote CMS', 'Touch UI', 'Speech Synthesis', 'WCAG Accessible'].map((tag) => (
                            <span key={tag} className="bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-xs px-2.5 py-1 rounded-full font-medium">{tag}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Video Walkthrough */}
                <div className="px-8 md:px-12 pb-8">
                  <div className="max-w-xl mx-auto rounded-lg overflow-hidden shadow-lg">
                    <video
                      src="/videos/uni-walkthrough.webm"
                      autoPlay
                      loop
                      muted
                      playsInline
                      poster="/portfolio-images/uni-installation.jpg"
                      className="w-full h-auto"
                    >
                      Your browser does not support the video tag.
                    </video>
                  </div>
                </div>
              </div>
            </div>

            {/* Case Study 3: Lincoln Home */}
            <div>
              <div className="bg-neutral-50 dark:bg-neutral-900 rounded-2xl overflow-hidden border border-neutral-200 dark:border-neutral-700">
                <div className="bg-gradient-to-r from-accent-700 to-accent-900 p-8 md:p-12">
                  <div className="text-accent-200 text-sm font-medium tracking-wide uppercase mb-3">Abraham Lincoln Home National Historic Site</div>
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">Multilingual Tablet Experience</h3>
                  <p className="text-accent-100 text-lg">Guided interactive experience in 12 languages for a National Historic Site</p>
                </div>

                {/* Image */}
                <div className="px-8 md:px-12 py-8">
                  <div className="max-w-3xl mx-auto rounded-lg overflow-hidden shadow-lg">
                    <img
                      src="/portfolio-images/lincoln-home.jpg"
                      alt="Abraham Lincoln Home National Historic Site Tablet Experience"
                      className="w-full h-auto"
                    />
                  </div>
                </div>

                <div className="p-8 md:p-12">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                    <div>
                      <h4 className="font-bold text-lg mb-4 text-neutral-900 dark:text-neutral-100">The challenge</h4>
                      <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed mb-6">
                        A historic site needed a tablet-based guided experience accessible to diverse visitors in multiple languages — including full audio narration and video — without relying on expensive ongoing translation subscriptions or constant network access.
                      </p>
                      <h4 className="font-bold text-lg mb-4 text-neutral-900 dark:text-neutral-100">The solution</h4>
                      <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed">
                        A React PWA installed on iPads. When editors change a paragraph, the build pipeline detects it and re-translates only that node into all 12 languages, generates audio narration for each, and switches video tracks to match the visitor's language — including right-to-left support for Arabic. No translation subscriptions, no ongoing costs.
                      </p>
                    </div>
                    <div>
                      <h4 className="font-bold text-lg mb-4 text-neutral-900 dark:text-neutral-100">Key features</h4>
                      <div className="space-y-4">
                        <div className="flex items-start gap-3">
                          <div className="mt-1 p-1.5 bg-accent-100 dark:bg-accent-900/40 rounded-lg">
                            <Languages size={18} className="text-accent-600 dark:text-accent-400" />
                          </div>
                          <div>
                            <div className="font-semibold text-neutral-900 dark:text-neutral-100">Automated 12-Language Translation</div>
                            <div className="text-sm text-neutral-500 dark:text-neutral-400">Build pipeline that only re-translates changed content nodes — no ongoing subscription costs</div>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="mt-1 p-1.5 bg-accent-100 dark:bg-accent-900/40 rounded-lg">
                            <Volume2 size={18} className="text-accent-600 dark:text-accent-400" />
                          </div>
                          <div>
                            <div className="font-semibold text-neutral-900 dark:text-neutral-100">AI Audio Narration</div>
                            <div className="text-sm text-neutral-500 dark:text-neutral-400">Auto-generated screen reader audio in all 12 languages with language-specific video switching</div>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="mt-1 p-1.5 bg-accent-100 dark:bg-accent-900/40 rounded-lg">
                            <Wifi size={18} className="text-accent-600 dark:text-accent-400" />
                          </div>
                          <div>
                            <div className="font-semibold text-neutral-900 dark:text-neutral-100">Progressive Web App</div>
                            <div className="text-sm text-neutral-500 dark:text-neutral-400">Installable on iPads, fully functional offline with all assets cached locally</div>
                          </div>
                        </div>
                      </div>
                      <div className="mt-6 pt-6 border-t border-neutral-200 dark:border-neutral-700">
                        <div className="text-xs text-neutral-400 dark:text-neutral-500 uppercase tracking-wide mb-2">Tech stack</div>
                        <div className="flex flex-wrap gap-2">
                          {['React', 'PWA', 'AI Translation', 'AI Audio Generation', 'iPad', 'Multilingual', 'RTL Support', 'Offline-First'].map((tag) => (
                            <span key={tag} className="bg-accent-100 dark:bg-accent-900/30 text-accent-700 dark:text-accent-300 text-xs px-2.5 py-1 rounded-full font-medium">{tag}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Recent Work */}
        <section className="py-24 px-6 bg-white dark:bg-neutral-800">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-16 text-center text-primary-700 dark:text-gray-100">
              Recent work
            </h2>

            <div className="relative rounded-2xl overflow-hidden border border-neutral-200 dark:border-neutral-700">
              {/* Atmospheric background */}
              <img
                src="/portfolio-images/smartdetect-environment.jpg"
                alt=""
                aria-hidden="true"
                className="absolute inset-0 w-full h-full object-cover opacity-[0.70] dark:opacity-[0.55] pointer-events-none"
              />

              <div className="relative p-8 md:p-12">
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-end">
                  {/* Left column - image (60%) */}
                  <div className="lg:col-span-3">
                    <div className="bg-white/85 dark:bg-neutral-900/75 backdrop-blur-md rounded-xl p-4">
                      <img
                        src="/portfolio-images/smartdetect-ui.jpg"
                        alt="John Deere SmartDetect Kiosk Interface showing three-panel product showcase"
                        className="w-full h-auto rounded-lg shadow-lg"
                      />
                      <p className="text-sm text-neutral-500 dark:text-neutral-300 italic mt-2">John Deere SmartDetect™ Kiosk Interface</p>
                    </div>
                  </div>

                  {/* Right column - details (40%) */}
                  <div className="lg:col-span-2">
                    <div className="bg-white/85 dark:bg-neutral-900/75 backdrop-blur-md rounded-xl p-6">
                      <div className="text-primary-600 dark:text-primary-400 text-sm font-medium tracking-wide uppercase mb-2">John Deere</div>
                      <h3 className="text-xl font-bold text-neutral-900 dark:text-white mb-4">SmartDetect™ Trade Show Kiosk</h3>
                      <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed mb-6">
                        Touch-screen kiosk built for John Deere's ConExpo 2026 booth. Three-panel product showcase for the SmartDetect safety technology suite, designed for high-traffic trade show environments.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {['WinUI 3', 'C#', '.NET', 'Touch UI', 'Kiosk Mode'].map((tag) => (
                          <span key={tag} className="bg-primary-100 dark:bg-primary-500/20 text-primary-700 dark:text-primary-300 text-xs px-2.5 py-1 rounded-full font-medium">{tag}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Capabilities */}
        <section id="capabilities" className="py-24 px-6 bg-white dark:bg-neutral-800">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center text-primary-700 dark:text-gray-100">
              Technical capabilities
            </h2>
            <p className="text-lg text-neutral-500 dark:text-neutral-400 text-center mb-16 max-w-2xl mx-auto">
              Every installation has different constraints. This is the standing toolkit.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  icon: <WifiOff size={24} />,
                  title: 'Offline-First Architecture',
                  description: 'Kiosks and tablets that run without a network. Content syncs when connectivity returns.'
                },
                {
                  icon: <Monitor size={24} />,
                  title: 'Remote CMS Integration',
                  description: 'Non-technical staff update content through WordPress, Elementor, or custom admin panels. Changes propagate to all devices automatically.'
                },
                {
                  icon: <Accessibility size={24} />,
                  title: 'Full Accessibility',
                  description: 'WCAG-compliant interfaces with adjustable contrast, font sizing, dyslexia-friendly fonts, and custom screen reader implementations.'
                },
                {
                  icon: <Languages size={24} />,
                  title: 'Multilingual Systems',
                  description: 'Automated AI translation pipelines that handle text, audio narration, and video tracks across any number of languages.'
                },
                {
                  icon: <AppWindow size={24} />,
                  title: 'Cross-Platform & Native',
                  description: 'Native WinUI 3 and WPF when kiosk hardware demands raw performance, multi-display support, or tight OS integration — plus Electron for Mac/Windows, PWAs for tablets, and React Native for mobile.'
                },
                {
                  icon: <Volume2 size={24} />,
                  title: 'AI-Powered Audio',
                  description: 'Automated narration generation in multiple languages, integrated with custom screen reader modes and on-screen highlighting.'
                },
              ].map((capability, index) => (
                <div key={index} className="bg-white dark:bg-neutral-800 p-6 rounded-2xl shadow-soft border border-neutral-200 dark:border-neutral-700 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                  <div className="p-3 bg-primary-100 dark:bg-primary-900/40 rounded-xl w-fit mb-4 text-primary-600 dark:text-primary-400">
                    {capability.icon}
                  </div>
                  <h3 className="text-lg font-bold mb-2 text-neutral-900 dark:text-neutral-100">{capability.title}</h3>
                  <p className="text-neutral-600 dark:text-neutral-400 text-sm leading-relaxed">{capability.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How I Work / For Agencies */}
        <section className="py-24 px-6 bg-white dark:bg-neutral-800">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-primary-700 dark:text-gray-100">
              Built for agency partnerships
            </h2>
            <p className="text-lg text-neutral-600 dark:text-neutral-300 leading-relaxed mb-12 max-w-3xl mx-auto">
              You own the client relationship, creative direction, and content. I build the technical layer and make sure it holds up in the field. Fifteen years of agency work, 150+ projects, for clients including Cisco, Dell, Discover Atlanta, and the University of Northern Iowa.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="p-6">
                <div className="text-4xl font-bold text-primary-600 dark:text-primary-400 mb-2">01</div>
                <h3 className="font-bold text-lg mb-2 text-neutral-900 dark:text-neutral-100">You design</h3>
                <p className="text-neutral-500 dark:text-neutral-400 text-sm">Your team creates the visual design, content strategy, and user experience. I consult on technical feasibility.</p>
              </div>
              <div className="p-6">
                <div className="text-4xl font-bold text-primary-600 dark:text-primary-400 mb-2">02</div>
                <h3 className="font-bold text-lg mb-2 text-neutral-900 dark:text-neutral-100">I build</h3>
                <p className="text-neutral-500 dark:text-neutral-400 text-sm">I develop the full technical layer — kiosk applications, CMS integrations, accessibility, multilingual pipelines, offline sync.</p>
              </div>
              <div className="p-6">
                <div className="text-4xl font-bold text-primary-600 dark:text-primary-400 mb-2">03</div>
                <h3 className="font-bold text-lg mb-2 text-neutral-900 dark:text-neutral-100">It just works</h3>
                <p className="text-neutral-500 dark:text-neutral-400 text-sm">Your client gets a reliable, accessible installation their staff can manage. No ongoing technical support needed.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-24 px-6 bg-primary-50 dark:bg-neutral-900">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center text-primary-700 dark:text-gray-100">Working on an installation?</h2>
            <p className="text-lg text-neutral-500 dark:text-neutral-400 text-center mb-12 max-w-2xl mx-auto">
              Early planning or approved designs — either is a fine time to talk.
            </p>
            <div className="bg-white dark:bg-neutral-800 p-8 rounded-2xl shadow-soft max-w-2xl mx-auto border border-primary-100 dark:border-neutral-700">
              <ContactForm />
            </div>
            <div className="text-center mt-12">
              <p className="text-lg dark:text-neutral-200">Or email me directly:</p>
              <a href="mailto:ben@bensmithdev.com" className="text-primary-600 dark:text-primary-400 font-semibold hover:text-accent-600 dark:hover:text-accent-400 transition-colors duration-200">ben@bensmithdev.com</a>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-neutral-900 dark:bg-black text-white py-12 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="mb-6 md:mb-0">
                <a href="/" className="text-2xl font-bold">
                  <span className="text-white">Ben Smith</span>
                  <span className="text-orange-500"> Development</span>
                </a>
                <p className="mt-2 text-primary-200 dark:text-primary-300">Interactive installations for museums & cultural institutions.</p>
              </div>
              <div className="flex space-x-6">
                <a
                  href="https://www.linkedin.com/in/benjaminchristophersmith/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-300 dark:text-primary-400 hover:text-accent-300 dark:hover:text-accent-400 transition-all duration-200 hover:scale-110 rounded-full p-2"
                  aria-label="LinkedIn Profile"
                >
                  <Linkedin size={24} />
                </a>
                <a
                  href="mailto:ben@bensmithdev.com"
                  className="text-primary-300 dark:text-primary-400 hover:text-accent-300 dark:hover:text-accent-400 transition-all duration-200 hover:scale-110 rounded-full p-2"
                  aria-label="Email ben@bensmithdev.com"
                >
                  <Mail size={24} />
                </a>
              </div>
            </div>
            <div className="border-t border-primary-700/30 dark:border-primary-600/30 mt-8 pt-8 text-center text-primary-300 dark:text-primary-400">
              <p>&copy; {new Date().getFullYear()} Benjamin Smith. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default KiosksPage;